import { useMemo, useState } from 'react';
import './productShowcaseCarousel.css';

type CarouselSlide = {
  src: string;
  alt: string;
};

type ProductShowcaseCarouselProps = {
  slides?: CarouselSlide[];
  className?: string;
};

export default function ProductShowcaseCarousel({ slides = [], className }: ProductShowcaseCarouselProps) {
  const safeSlides = useMemo(() => slides, [slides]);
  const [activeIndex, setActiveIndex] = useState(0);
  const hasMultipleSlides = safeSlides.length > 1;

  const goToSlide = (index: number) => {
    const normalized = ((index % safeSlides.length) + safeSlides.length) % safeSlides.length;
    setActiveIndex(normalized);
  };

  const rootClassName = ['rid-product-showcase-carousel', className].filter(Boolean).join(' ');

  return (
    <div className={rootClassName} data-node-id="4224:18339" data-testid="landing-product-carousel">
      <div className="rid-product-showcase-carousel__viewport">
        <div className="rid-product-showcase-carousel__track" style={{ transform: `translateX(-${activeIndex * 100}%)` }}>
          {safeSlides.map((slide, index) => (
            <div className="rid-product-showcase-carousel__slide" key={`${slide.src}-${index}`} data-testid={`landing-product-carousel-slide-${index + 1}`} data-selected={index === activeIndex}>
              <img src={slide.src} alt={slide.alt} />
            </div>
          ))}
        </div>

        {hasMultipleSlides ? (
          <>
            <button
              type="button"
              className="rid-product-showcase-carousel__control rid-product-showcase-carousel__control--prev"
              onClick={() => goToSlide(activeIndex - 1)}
              aria-label="Previous slide"
              data-testid="landing-product-carousel-previous"
            >
              ‹
            </button>
            <button
              type="button"
              className="rid-product-showcase-carousel__control rid-product-showcase-carousel__control--next"
              onClick={() => goToSlide(activeIndex + 1)}
              aria-label="Next slide"
              data-testid="landing-product-carousel-next"
            >
              ›
            </button>
          </>
        ) : null}
      </div>

      {hasMultipleSlides ? (
        <div className="rid-product-showcase-carousel__dots" role="tablist" aria-label="Carousel slide navigation" data-testid="landing-product-carousel-navigation">
          {safeSlides.map((slide, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={`${slide.src}-${index}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`rid-product-showcase-carousel__dot ${isActive ? 'rid-product-showcase-carousel__dot--active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                data-testid={`landing-product-carousel-dot-${index + 1}`}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
